import type { LoginResponseDto } from '@citilight-monitor/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ApiClient } from './api/ApiClient';
import { storage } from './storage';

const SESSION_KEY = 'citilight-monitor:session';

interface AuthContextValue {
  username: string | null;
  api: ApiClient;
  signIn(identifier: string, password: string): Promise<void>;
  signOut(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readSession(): LoginResponseDto | null {
  try {
    const session = JSON.parse(storage.get(SESSION_KEY) ?? 'null') as LoginResponseDto | null;
    return session && new Date(session.expiresAt) > new Date() ? session : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState(readSession);

  const signOut = useCallback(() => {
    storage.remove(SESSION_KEY);
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const api = useMemo(() => new ApiClient(() => readSession()?.token ?? null, signOut), [signOut]);

  const signIn = useCallback(
    async (identifier: string, password: string) => {
      const next = await api.login({ identifier, password });
      storage.set(SESSION_KEY, JSON.stringify(next));
      setSession(next);
    },
    [api],
  );

  const value = useMemo(
    () => ({ username: session?.username ?? null, api, signIn, signOut }),
    [session, api, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
